import CancelIcon from '@mui/icons-material/Cancel';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { FC } from 'react';

import InfoTooltip from '../InfoTooltip';

const FormSelectInput: FC<{
  disabled?: boolean;
  error: any;
  helperText?: string;
  infoTooltip?: string;
  label?: string;
  multiple?: boolean;
  name: string;
  onBlur?: any;
  onChange?: any;
  readonly?: boolean;
  ref?: any;
  required?: boolean;
  size?: 'small' | 'medium';
  value?: any;
  values: Array<{
    label: string;
    value: any;
    disabled?: boolean;
  }>;
}> = (props) => {
  const {
    disabled = false,
    error,
    helperText = '',
    infoTooltip,
    label,
    multiple = false,
    name,
    onBlur,
    onChange,
    readonly = false,
    required = false,
    size,
    value,
    values,
  } = props;

  // Fixed: Handle boolean false and other falsy values properly
  const selectorValue = multiple
    ? value || []
    : value !== undefined &&
        values.some(({ value: arrValue }) => arrValue === value)
      ? value
      : '';
  const inputSize = size || 'small';

  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      <FormControl sx={{ width: '100%' }} error={error} size={inputSize}>
        <InputLabel id={name} size={inputSize}>
          {label}
          {required && '*'}
        </InputLabel>
        <Select
          disabled={disabled}
          error={error}
          inputProps={{ 'data-testid': name }}
          label={label}
          labelId={name}
          multiple={multiple}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          readOnly={readonly}
          renderValue={(selected: any) => {
            if (!multiple) {
              const option = values.find((e) => e.value === selected);
              if (
                selected === '' ||
                selected === null ||
                selected === undefined
              ) {
                return '';
              }
              return option ? option.label : '';
            }
            const selectedLabels = selected.flatMap((value) => {
              const option = values.find((item) => item.value === value);
              return option ? [option.label] : [];
            });

            if (selectedLabels.length === 0) {
              return '';
            }

            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedLabels.map((selectedLabel) => (
                  <Chip
                    key={selectedLabel}
                    label={selectedLabel}
                    size="small"
                  />
                ))}
              </Box>
            );
          }}
          size={inputSize}
          value={selectorValue}
        >
          {!required &&
            ((value && !Array.isArray(value)) ||
              (Array.isArray(value) && value.length > 0)) && (
              <MenuItem
                value={''}
                onMouseDown={(e) => {
                  if (multiple) {
                    e.preventDefault();
                    onChange?.({ target: { name, value: [] } });
                  }
                }}
              >
                <Typography component="div">
                  <Box
                    sx={{
                      fontStyle: 'italic',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Clear Field
                  </Box>
                </Typography>
              </MenuItem>
            )}
          {values.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {multiple && (
                <Checkbox checked={value?.indexOf(option.value) > -1} />
              )}
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      {infoTooltip && <InfoTooltip infoMessage={infoTooltip} />}
    </Stack>
  );
};

export default FormSelectInput;
